import { ArrowLeft } from 'assets/icons/ArrowLeft'
import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Pagination } from 'components/Pagination/Pagination'
import { FilterEnum, LinkEnum } from 'components/Filter/SingleFilter'
import { Td } from 'components/Td/Td'
import { clearFleetMachinesList, getFleetMachines } from 'features/machinery/fleetMachinesSlice'
import useQueryPagination from 'hooks/useQueryPagination'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { RootState } from 'store'
import {
  Button,
  EditContainer,
  Flex,
  H1WithBack,
  PageFooter,
  PageWrapper,
  Table,
  Th,
  Tr,
} from 'styling/GlobalStyles'
import { getParams, handlePagination } from 'utils/helpers'
import styled from 'styled-components'

const operationalStatusMap: Record<number, { label: string; color: string }> = {
  1: { label: 'Idle', color: '#6b7280' },
  2: { label: 'Running', color: '#3b82f6' },
}

const availabilityStatusMap: Record<number, { label: string; color: string }> = {
  1: { label: 'Available', color: '#22c55e' },
  2: { label: 'In Use', color: '#f97316' },
}

const healthStatusMap: Record<number, { label: string; color: string }> = {
  1: { label: 'Healthy', color: '#22c55e' },
  2: { label: 'Broken', color: '#ef4444' },
  3: { label: 'In Maint.', color: '#eab308' },
}

const StatusBadge = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 500;
  color: ${({ color }) => color};
  border: 1px solid ${({ color }) => color};
  white-space: nowrap;
`

const BadgesWrap = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`

const FuelText = styled.span<{ value: number }>`
  color: ${({ value }) =>
    value <= 20 ? '#ef4444' : value <= 40 ? '#f97316' : 'inherit'};
  font-size: 0.85rem;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`

const CountText = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colorGray ?? '#9ca3af'};
  white-space: nowrap;
  margin-left: 8px;
`

export const FleetMachines = () => {
  const [params, setParams] = useState<any>(getParams({ location }))
  const navigate = useNavigate()
  const { machines, totalPages, totalCount, noMachines } = useSelector(
    (state: RootState) => state.fleetMachines,
  )

  useQueryPagination({
    fetchFunctions: [getFleetMachines],
    cleanUpFunctions: [clearFleetMachinesList],
    params,
  })

  const filters = [
    {
      type: FilterEnum.ASYNC_SELECT,
      name: 'Category',
      link: LinkEnum.MACHINE_CATEGORIES,
      propName: 'machineCategoryIds',
    },
    {
      type: FilterEnum.SELECT,
      name: 'Health',
      propName: 'healthStatuses',
      options: [
        { label: 'Healthy', value: 1 },
        { label: 'Broken', value: 2 },
        { label: 'In Maintenance', value: 3 },
      ],
    },
    {
      type: FilterEnum.SELECT,
      name: 'Availability',
      propName: 'availabilityStatuses',
      options: [
        { label: 'Available', value: 1 },
        { label: 'In Use', value: 2 },
      ],
    },
  ]

  return (
    <>
      <NavBar
        input
        placeholder='Search by name or model...'
        params={params}
        setParams={setParams}
        filterOptions={filters}
        visibleFilterOverflow
        enableTemporaryManualClosing
      />
      <PageWrapper>
        <HeaderRow>
          <H1WithBack title='Fleet' arrow link='/machinery/fleet-overview' />
          <HeaderActions>
            <Button orange onClick={() => navigate('/machinery/fleet/categories')}>
              Categories
            </Button>
            <Button orange onClick={() => navigate('/machinery/add')}>
              Add Machine
            </Button>
          </HeaderActions>
        </HeaderRow>

        {noMachines ? (
          <NoItems marginTop='40px' title='Machines' />
        ) : (
          <Table>
            <Th>
              <Td w='12%' noTooltip text='Name' />
              <Td w='13%' noTooltip text='Category' />
              <Td w='14%' noTooltip text='Model' />
              <Td w='25%' noTooltip text='State (Op-Avail-Health)' />
              <Td w='13%' noTooltip text='Operator' />
              <Td w='13%' noTooltip text='Location' />
              <Td w='7%' noTooltip text='Fuel' />
              <Td w='3%' options />
            </Th>

            {machines.map((item) => {
              const opStatus = operationalStatusMap[item.operationalStatus]
              const avStatus = availabilityStatusMap[item.availabilityStatus]
              const hlStatus = healthStatusMap[item.healthStatus]
              const operatorName = item.operator
                ? `${item.operator.firstName} ${item.operator.lastName}`
                : '-'

              return (
                <Tr
                  view
                  onClick={() => navigate('/machinery/machine/' + item.id)}
                  key={item.id}
                >
                  <Td w='12%' text={item.name} />
                  <Td w='13%' text={item.category?.name ?? '-'} />
                  <Td w='14%' text={item.machineModel?.name ?? '-'} />
                  <Td w='25%' noTooltip>
                    <BadgesWrap>
                      {opStatus && (
                        <StatusBadge color={opStatus.color}>{opStatus.label}</StatusBadge>
                      )}
                      {avStatus && (
                        <StatusBadge color={avStatus.color}>{avStatus.label}</StatusBadge>
                      )}
                      {hlStatus && (
                        <StatusBadge color={hlStatus.color}>{hlStatus.label}</StatusBadge>
                      )}
                    </BadgesWrap>
                  </Td>
                  <Td w='13%' text={operatorName} />
                  <Td w='13%' text={item.location?.name ?? '-'} />
                  <Td w='7%' noTooltip>
                    <FuelText value={item.fuelPercentage}>
                      {item.fuelPercentage}%
                    </FuelText>
                  </Td>
                  <Td w='3%' options noTooltip>
                    <EditContainer onClick={() => navigate('/machinery/machine/' + item.id)}>
                      <Flex style={{ transform: 'rotate(180deg)' }} className='arrow-table'>
                        <ArrowLeft />
                      </Flex>
                    </EditContainer>
                  </Td>
                </Tr>
              )
            })}
          </Table>
        )}

        {totalCount > 0 && (
          <Flex style={{ justifyContent: 'flex-end', marginTop: 8 }}>
            <CountText>Showing {machines.length} of {totalCount}</CountText>
          </Flex>
        )}
      </PageWrapper>
      <PageFooter>
        <Pagination
          setPage={(page) => handlePagination(page, setParams, 30)}
          pageCount={Math.ceil(totalPages)}
          forcePage={(params.page ? params.page : 1) - 1}
        />
      </PageFooter>
    </>
  )
}
